import React, { forwardRef, useImperativeHandle, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import { setValueCustomerStories } from '../../redux/productSlice';
import CustomInput from '../../components/customInput/CustomInput';
import ModalFileUpload from '../../components/ModalFileUpload/ModalFileUpload';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';
import { uploadAsset, deleteAsset } from '../../redux/assetSlice';
import { ASSET_LOCATION_MAPPING, ASSET_TYPE_MAPPING, LABEL_DELETE_ASSET } from '../../utils/constant';
import { isEmpty } from '../../utils/commonUtil';

const CustomerStories = forwardRef(({ language, productId, formType, onShowAlert }, ref) => {
  const dispatch = useDispatch();
  const productState = useSelector((state) => state.product);
  const product = productState.data || {};

  const customerStoriesData = product.customerStories || product.contentData?.customerStories || {};
  const customerStoriesItems = customerStoriesData.items || [];
  const customerStoriesTitle = customerStoriesData.title || '';

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [isShowDeleteAsset, setIsShowDeleteAsset] = useState(null);
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [showDeleteStoryModal, setShowDeleteStoryModal] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);

  const PLACEHOLDER_CONFIG = {
    title: {
      EN: "Enter customer stories title EN",
      CN: "Enter customer stories title CN"
    },
    logo: {
      EN: "upload logo",
      CN: "upload logo"
    },
    stories: {
      EN: "Enter stories content EN",
      CN: "Enter stories content CN"
    },
    name: {
      EN: "Enter name EN",
      CN: "Enter name CN"
    },
    position: {
      EN: "Enter position EN",
      CN: "Enter position CN"
    },
    company: {
      EN: "Enter company EN",
      CN: "Enter company CN"
    },
    borderColor: {
      EN: "Enter border color EN ",
      CN: "Enter border color CN"
    }
  };

  const INITIAL_STORY_ITEM = {
    logo: '',
    logoFileId: '',
    stories: '',
    name: '',
    position: '',
    company: '',
    borderColor: ''
  };

  useEffect(() => {
    if (!customerStoriesData.title && customerStoriesItems.length === 0) {
      dispatch(setValueCustomerStories({
        value: {
          title: '',
          items: [INITIAL_STORY_ITEM]
        }
      }));
    }
  }, [customerStoriesData, customerStoriesItems.length, dispatch]);

  const handleFileUpload = (file) => {
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("uuid", formType === 'EDIT' ? productId : "");
    formData.append("assetLocation", ASSET_LOCATION_MAPPING[selectedField]);
    formData.append("assetType", ASSET_TYPE_MAPPING.product);
    formData.append("file", file);

    dispatch(uploadAsset(formData))
      .then((res) => {
        if (res?.payload) {
          const fileId = res.payload.resdata.fileId;
          const fileUrl = res.payload.resdata.url;

          dispatch((dispatch, getState) => {
            const state = getState();
            const product = state.product.data || {};
            const customerStoriesData = product.customerStories || product.contentData?.customerStories || {};
            const currentStoryItems = customerStoriesData.items || [];

            const newItems = currentStoryItems.map((item, i) => {
              if (i === selectedStoryIndex) {
                return {
                  ...item,
                  logo: fileUrl,
                  logoFileId: fileId
                };
              }
              return item;
            });

            dispatch(setValueCustomerStories({
              value: {
                ...customerStoriesData,
                items: newItems
              }
            }));
          });
        }
        if (res?.error) {
          onShowAlert(res.error.message, false);
        }
      })
      .catch((err) => {
        onShowAlert(err.message || "Upload failed", false);
      })
      .finally(() => {
        setIsUploading(false);
        setShowFileUploadModal(false);
        setSelectedStoryIndex(null);
        setSelectedField(null);
      });
  };

  const handleDeleteFile = () => {
    const fileId = customerStoriesItems[selectedStoryIndex]?.logoFileId;

    const newItems = customerStoriesItems.map((item, i) => {
      if (i === selectedStoryIndex) {
        return {
          ...item,
          logo: "",
          logoFileId: ""
        };
      }
      return item;
    });

    dispatch(setValueCustomerStories({
      value: {
        ...customerStoriesData,
        items: newItems
      }
    }));

    if (fileId) {
      dispatch(deleteAsset(fileId))
        .then((res) => {
          if (res?.error &&
            !res.error.message.includes("No active document") &&
            !res.error.message.includes("Unexpected token")) {
            onShowAlert(res.error.message, false);
          }
        })
        .catch((err) => {
          if (err.message &&
            !err.message.includes("No active document") &&
            !err.message.includes("Unexpected token")) {
            onShowAlert(err.message, false);
          }
        })
        .finally(() => {
          setIsShowDeleteAsset(null);
          setSelectedStoryIndex(null);
          setSelectedField(null);
        });
    } else {
      setIsShowDeleteAsset(null);
      setSelectedStoryIndex(null);
      setSelectedField(null);
    }
  };

  const handleShowUploadFile = (storyIndex, field) => {
    setSelectedStoryIndex(storyIndex);
    setSelectedField(field);
    setShowFileUploadModal(true);
  };

  const handleShowDeleteFile = (storyIndex, field) => {
    setSelectedStoryIndex(storyIndex);
    setSelectedField(field);
    setIsShowDeleteAsset(LABEL_DELETE_ASSET);
  };

  const handleCancelUploadFile = () => {
    setShowFileUploadModal(false);
    setSelectedStoryIndex(null);
    setSelectedField(null);
  };

  const addGroup = () => {
    dispatch(setValueCustomerStories({
      value: {
        ...customerStoriesData,
        items: [...customerStoriesItems, INITIAL_STORY_ITEM]
      }
    }));
  };

  const deleteGroup = (index) => {
    if (customerStoriesItems.length <= 1) {
      alert("At least retain one customer story");
      return;
    }
    setStoryToDelete(index);
    setShowDeleteStoryModal(true);
  };

  const handleConfirmDeleteStory = () => {
    if (storyToDelete !== null) {
      const newItems = customerStoriesItems.filter((_, i) => i !== storyToDelete);
      dispatch(setValueCustomerStories({
        value: {
          ...customerStoriesData,
          items: newItems
        }
      }));
    }
    setShowDeleteStoryModal(false);
    setStoryToDelete(null);
  };

  const updateCustomerStories = (field, value) => {
    dispatch(setValueCustomerStories({
      value: { ...customerStoriesData, [field]: value }
    }));
  };

  const updateStoryItem = (index, field, value) => {
    const newItems = customerStoriesItems.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    dispatch(setValueCustomerStories({
      value: {
        ...customerStoriesData,
        items: newItems
      }
    }));
  };

  const validateAllCustomerStories = () => ({});

  useImperativeHandle(ref, () => ({
    addGroup,
    validateAllCustomerStories
  }));

  const renderFileUploadSection = (item, index) => (
    <div className='package-input'>
      <label>logo:</label>
      <input
        type="text"
        placeholder={PLACEHOLDER_CONFIG.logo[language]}
        value={item.logo || ''}
        disabled
        style={{ width: '73%' }}
      />
      {!item.logo ? (
        <button
          className="upload-btn"
          onClick={() => handleShowUploadFile(index, "logo")}
        >
          Upload
        </button>
      ) : (
        <button
          className="icon-btn"
          onClick={() => handleShowDeleteFile(index, "logo")}
        >
          <img src={DeleteIcon} alt="Delete" className="action-icon" />
        </button>
      )}
    </div>
  );

  const renderStoryItem = (item, index) => (
    <div key={index}>
      <div className="domain-group">

        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>Name:</label>
          </div>
          <CustomInput
            type="text"
            value={item.name || ''}
            onChange={(e) => updateStoryItem(index, 'name', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.name[language]}
            customStyle={{ width: '73%' }}
          />
          <button
            className="icon-btn"
            onClick={() => deleteGroup(index)}
          >
            <img src={DeleteIcon} alt="Delete" className="campaign-delete-icon" />
          </button>
        </div>

        <div className='package-input'>
          <label>Stories:</label>
          <textarea
            placeholder={PLACEHOLDER_CONFIG.stories[language]}
            value={item.stories || ''}
            onChange={(e) => updateStoryItem(index, 'stories', e.target.value)}
            rows={4}
            style={{ width: '73%' }}
          />
        </div>

        {renderFileUploadSection(item, index)}

        {item.logo && (
          <div style={{ marginLeft: '190px', marginBottom: '20px' }}>
            <img
              src={`https://uat.stack-ez.com${item.logo}`}
              alt="Preview"
              style={{ maxWidth: '200px', maxHeight: '200px', marginTop: '10px' }}
            />
          </div>
        )}

        <div className='package-input'>
          <label>Position:</label>
          <input
            type="text"
            placeholder={PLACEHOLDER_CONFIG.position[language]}
            value={item.position || ''}
            onChange={(e) => updateStoryItem(index, 'position', e.target.value)}
            style={{ width: '73%' }}
          />
        </div>

        <div className='package-input'>
          <label>Company:</label>
          <input
            type="text"
            placeholder={PLACEHOLDER_CONFIG.company[language]}
            value={item.company || ''}
            onChange={(e) => updateStoryItem(index, 'company', e.target.value)}
            style={{ width: '73%' }}
          />
        </div>

        <div className='package-input'>
          <label>Border Color:</label>
          <input
            type="text"
            placeholder={PLACEHOLDER_CONFIG.borderColor[language]}
            value={item.borderColor}
            onChange={(e) => updateStoryItem(index, 'borderColor', e.target.value)}
            style={{ width: '73%' }}
          />
        </div>

      </div>

      {index !== customerStoriesItems.length - 1 && (
        <div
          style={{
            height: '0.5px',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            margin: '25px -40px',
            width: 'calc(100% + 80px)',
            display: 'block',
            boxSizing: 'border-box'
          }}
        />
      )}
    </div>
  );

  return (
    <div className="tab-package-container">
      <div className="domain-group">
        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>Title:</label>
          </div>
          <CustomInput
            type="text"
            value={customerStoriesTitle}
            onChange={(e) => updateCustomerStories('title', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.title[language]}
            customStyle={{ width: '73%' }}
          />
        </div>
      </div>

      <div
        style={{
          height: '0.5px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          margin: '25px -40px',
          width: 'calc(100% + 80px)',
          display: 'block',
          boxSizing: 'border-box'
        }}
      />

      {customerStoriesItems.map(renderStoryItem)}

      <ModalFileUpload
        show={showFileUploadModal}
        handleCancel={handleCancelUploadFile}
        handleUpload={handleFileUpload}
        isLoading={isUploading}
      />

      <CommonDeleteModal
        show={!isEmpty(isShowDeleteAsset)}
        onHide={() => setIsShowDeleteAsset(null)}
        onConfirm={handleDeleteFile}
      />

      <CommonDeleteModal
        show={showDeleteStoryModal}
        onHide={() => {
          setShowDeleteStoryModal(false);
          setStoryToDelete(null);
        }}
        onConfirm={handleConfirmDeleteStory}
        title="Delete Customer Story"
        instruction="Are you sure you want to delete this customer story?"
      />
    </div>
  );
});

export default CustomerStories;